import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cleanFileName, storageUsagePercentage } from '../helpers';
import { PDFDocument } from 'pdf-lib';
import * as mammoth from 'mammoth';
import {
  Modal,
  message,
  Typography,
  Button,
  Input,
  Space,
  Tag,
  Form,
  Select,
  Upload,
  Popover,
  Table,
  Spin,
  Progress,
  Tooltip,
} from 'antd';
import {
  CloudUploadOutlined,
  GoogleOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { createDataSource } from '../../../reduxToolkit/services/corpus.service';
import {
  CONNECTOR_CONFIG,
  NOTES,
  PLACEHOLDER_COPY,
  TITLE_COPY,
  WEBSITE_TYPE_OPTIONS,
} from '../constants/dataSourceModal';
import { preSendUrl } from '../../../api/Api';
import { selectCorpusUsageMetrics } from '../../../reduxToolkit/corpusSlice';

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  );
};

const UploadView = ({
  onChangeView,
  onSubmit,
  brandId,
  setFileUploading,
  fileUploading,
}) => {
  const [uploading, setUploading] = useState(false);
  const corpusUsageMetrics = useSelector(selectCorpusUsageMetrics);

  const validateFile = (file) => {
    // Check if file is empty
    if (file.size === 0) {
      message.error(`${file.name} is empty. Please select a valid file.`);
      return false;
    }

    // File type and size limits (in bytes)
    const fileTypes = {
      // Standard RAG Supported Documents (50 MB Limit)
      '.pdf': 50 * 1024 * 1024, // PDF file (Maximum 50 MB)
      '.docx': 50 * 1024 * 1024, // Microsoft Word documents (Maximum 50 MB)

      // Standard RAG Supported Documents (10 MB Limit)
      '.html': 10 * 1024 * 1024, // HTML file (Maximum 10 MB)
      '.json': 10 * 1024 * 1024, // JSON file (Maximum 10 MB)
      '.jsonl': 10 * 1024 * 1024, // JSONL file (Maximum 10 MB)
      '.ndjson': 10 * 1024 * 1024, // NDJSON file (Maximum 10 MB)
      '.md': 10 * 1024 * 1024, // Markdown file (Maximum 10 MB)
      '.pptx': 10 * 1024 * 1024, // Microsoft PowerPoint slides (Maximum 10 MB)
      '.txt': 10 * 1024 * 1024, // Text file (Maximum 10 MB)
    };

    // Get file extension
    const fileExt = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    // Check if file type is supported
    if (!fileTypes[fileExt]) {
      message.error(`File type ${fileExt} is not supported.`);
      return false;
    }

    // Check file size limit
    const maxSize = fileTypes[fileExt];
    // console.log({ maxSize, fsz: file.size });

    if (file.size > maxSize) {
      message.error(
        `File size exceeds the maximum limit of ${formatFileSize(
          maxSize,
        )} for ${fileExt} files.`,
      );
      return Upload.LIST_IGNORE;
    }

    // If all validations pass, return true to proceed with upload
    return true;
  };

  const beforeUpload = (file) => {
    // First, run our custom validation
    const validationResult = validateFile(file);
    if (validationResult !== true) {
      return Upload.LIST_IGNORE;
    }

    // Then check MIME types and extensions
    const allowed = [
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const extAllowed = [
      'pdf',
      'docx',
      'html',
      'json',
      'jsonl',
      'ndjson',
      'md',
      'pptx',
      'txt',
    ];
    const ext = file.name.split('.').pop()?.toLowerCase();
    const ok = allowed.includes(file.type) || extAllowed.includes(ext);
    if (!ok) {
      message.error('Only PDF, DOC, DOCX, and TXT files are allowed.');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const checkPageCount = async (file) => {
    const MAX_PAGES = 500;
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (ext === 'pdf') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        if (pdfDoc.getPageCount() > MAX_PAGES) {
          throw new Error(`PDF files are limited to ${MAX_PAGES} pages.`);
        }
      } catch (error) {
        console.error('Error checking PDF page count:', error);
        throw new Error(
          `Invalid PDF file or exceeds page limit: ${error.message}`,
        );
      }
    } else if (['docx', 'doc'].includes(ext)) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        // Approximate page count (assuming ~500 words per page)
        const wordCount = result.value.split(/\s+/).length;
        const pageCount = Math.ceil(wordCount / 500);

        if (pageCount > MAX_PAGES) {
          throw new Error(
            `Document files are limited to approximately ${MAX_PAGES} pages.`,
          );
        }
      } catch (error) {
        console.error('Error checking document page count:', error);
        throw new Error(
          `Invalid document file or exceeds page limit: ${error.message}`,
        );
      }
    }
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      if (!file || file.size === 0) {
        message.error('Selected file is empty.');
        onError?.(new Error('Empty file'));
        return;
      }
      setUploading(true);
      setFileUploading(true);

      // Check page count for PDF and DOCX files
      await checkPageCount(file);

      const ext = file.name.split('.').pop()?.toLowerCase();
      const fileName = cleanFileName(file.name, 'document');
      const presign = await preSendUrl(ext, {
        brand_id: brandId,
        path: 'data-sources',
        file_name: fileName,
      });
      if (!(presign?.status === 200 && presign?.data?.response)) {
        throw new Error('Failed to prepare upload.');
      }

      const fileTypeMap = {
        pdf: 'application/pdf',
        txt: 'text/plain',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      };
      const contentType =
        fileTypeMap[ext] || file.type || 'application/octet-stream';

      const putResp = await fetch(presign.data.response, {
        method: 'PUT',
        headers: { contentType },
        body: file,
      });
      if (!putResp.ok) throw new Error('Upload failed.');
      const uploadedUrl = putResp.url?.split('?').shift();
      if (!uploadedUrl) throw new Error('Could not resolve uploaded URL.');

      onSuccess?.({}, file);

      // Submit as a plain text/doc/pdf datasource by URL
      onSubmit?.({
        d_source_type: 'LOCAL_FILE_UPLOAD',
        sources: [
          {
            url: uploadedUrl,
            d_source_name: fileName,
          },
        ],
      });
      message.success('File uploaded successfully.');
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'Upload failed.');
      onError?.(e);
    } finally {
      setUploading(false);
      setFileUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-3xl border border-gray-100 bg-white p-6 text-gray-900 shadow-sm relative">
        <div className="absolute z-10 top-8 right-8">
          <FileTypesPopover />
        </div>
        <Upload.Dragger
          name="file"
          multiple={false}
          maxCount={1}
          accept={'.pdf,.docx,.html,.json,.jsonl,.ndjson,.md,.pptx,.txt'}
          beforeUpload={(file) => {
            return beforeUpload(file);
          }}
          customRequest={customRequest}
          showUploadList={false}
          disabled={uploading || fileUploading}
          className="p-0 border-0 bg-transparent"
        >
          <div className="flex flex-col items-center gap-4 rounded-2xl bg-gray-50 py-8 cursor-pointer transition-colors">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
              <CloudUploadOutlined className="text-3xl" />
            </div>
            <div className="text-center">
              <Title level={4} className="!mb-1">
                Upload sources
              </Title>
              <Text className="text-center" type="secondary">
                Drag and drop or choose a file to upload
              </Text>
            </div>
            <div className=" text-center">
              <p className="text-indigo-500 font-medium">
                Click or drag file to this area
              </p>
            </div>
          </div>
        </Upload.Dragger>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {' '}
          {/* make sm:grid-cols-2 to sm:grid-cols-3 when implementing google drive and uncomments google workspace connector*/}
          {CONNECTOR_CONFIG.map((connector) => (
            <button
              key={connector.key}
              type="button"
              onClick={() => onChangeView?.(connector.key)}
              className="flex flex-col items-start gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-left transition hover:bg-white"
            >
              <Space size={12}>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                  {connector.icon}
                </div>
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    {connector.label}
                  </div>
                  <div className="text-sm text-gray-500">
                    {connector.description}
                  </div>
                </div>
              </Space>
              <Space size={8} wrap>
                {connector.pills?.map((pill) => (
                  <Tag
                    key={pill}
                    color="purple"
                    className="!m-0 !rounded-full !border-indigo-100 !bg-indigo-50 !text-indigo-600"
                  >
                    {pill}
                  </Tag>
                ))}
              </Space>
            </button>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-2 text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex w-full">
            <div className="min-w-[100px] me-2">
              <Text className="text-sm">Storage limit</Text>
              <Tooltip
                color="white"
                title={
                  <div>
                    <Text className="text-black">
                      Used{' '}
                      {
                        storageUsagePercentage(
                          corpusUsageMetrics?.storage_usage,
                        ).current
                      }{' '}
                      of{' '}
                      {
                        storageUsagePercentage(
                          corpusUsageMetrics?.storage_usage,
                        ).total
                      }
                    </Text>
                  </div>
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <div className="w-full">
              <div>
                <Progress
                  percent={
                    storageUsagePercentage(corpusUsageMetrics?.storage_usage)
                      .percentage
                  }
                  strokeColor={{
                    '0%': '#87d068',
                    '100%': '#e9101071',
                  }}
                />
              </div>
            </div>
            {/* <Text type="secondary">0/50</Text> */}
          </div>
        </div>
      </div>
    </div>
  );
};

const LinkView = ({ onSubmit, loading }) => {
  const [form] = Form.useForm();
  const sources = Form.useWatch('sources', form);
  const listContentRef = useRef(null);
  const [listHeight, setListHeight] = useState(null);

  const isValidUrl = (value) => {
    if (!value) return false;
    try {
      const parsed = new URL(value.trim());
      return Boolean(parsed.protocol && parsed.host);
    } catch (error) {
      return false;
    }
  };

  const extractDomain = (url) => {
    try {
      const domain = new URL(url.trim()).hostname
        .replace('www.', '')
        .split('.')[0];
      return domain.charAt(0).toUpperCase() + domain.slice(1); // Capitalize first letter
    } catch (e) {
      return '';
    }
  };

  // Used to updated the name of the website URL
  const handleWebsiteUrlChange = (index) => {
    const sources = form.getFieldValue('sources') || [];
    const currentSource = sources[index];

    const newSources = [...sources];

    if (currentSource?.url && isValidUrl(currentSource.url)) {
      const domain = extractDomain(currentSource.url);
      if (domain) {
        newSources[index] = {
          ...currentSource,
          d_source_name: domain,
        };
      }
    } else if (currentSource) {
      // Clear the name if URL is invalid
      newSources[index] = {
        ...currentSource,
        d_source_name: '',
      };
    }

    form.setFieldsValue({ sources: newSources });
  };

  const handleFinish = (values) => {
    const sources = (values?.sources || []).map((item) => ({
      d_source_name: item?.d_source_name?.trim(),
      url: item?.url?.trim(),
      website_type: item?.website_type,
    }));

    const invalidEntry = sources.find(
      (item) =>
        !item.d_source_name ||
        !item.url ||
        !item.website_type ||
        !isValidUrl(item.url),
    );

    if (!sources.length) {
      message.warning('Please provide at least one datasource.');
      return;
    }

    if (invalidEntry) {
      message.warning(
        'Please provide a valid name, URL, and website type for each entry.',
      );
      return;
    }

    onSubmit?.({
      d_source_type: 'WEBSITE_URL',
      sources,
    });
  };

  useLayoutEffect(() => {
    if (!listContentRef.current) return;
    const measuredHeight =
      listContentRef.current.getBoundingClientRect().height;
    setListHeight(measuredHeight);
  }, [sources?.length]);

  return (
    <>
      <style>
        {`
          @keyframes datasourceRowEnter {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-datasource-row {
            animation: datasourceRowEnter 0.22s ease-out;
          }
        `}
      </style>
      <Form
        form={form}
        layout="vertical"
        className="flex flex-col gap-6 text-gray-900"
        onFinish={handleFinish}
        initialValues={{
          sources: [
            {
              d_source_name: '',
              url: '',
              website_type: undefined,
            },
          ],
        }}
      >
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div
            className="relative overflow-hidden"
            style={{
              height: listHeight === null ? 'auto' : `${listHeight}px`,
              transition: listHeight === null ? undefined : 'height 0.28s ease',
            }}
          >
            <div ref={listContentRef}>
              <Form.List name="sources">
                {(fields, { add, remove }) => (
                  <div className="flex flex-col gap-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.key}
                        className="animate-datasource-row flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 shadow-sm transition-all duration-200 ease-out md:flex-row md:items-end"
                      >
                        <Form.Item
                          {...field}
                          name={[field.name, 'd_source_name']}
                          label={index === 0 ? 'Name' : ''}
                          rules={[
                            {
                              required: true,
                              message: 'Enter a datasource name.',
                            },
                          ]}
                          className="flex-1"
                        >
                          <Input
                            placeholder="Datasource name"
                            className="border border-gray-200"
                            allowClear
                            disabled={true}
                          />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, 'url']}
                          label={index === 0 ? 'URL' : ''}
                          rules={[{ required: true, message: 'Enter a URL.' }]}
                          className="flex-[1.5]"
                        >
                          <Input
                            placeholder="https://example.com"
                            className="border border-gray-200"
                            allowClear
                            onChange={() => handleWebsiteUrlChange(field.name)}
                          />
                        </Form.Item>

                        <Form.Item
                          {...field}
                          name={[field.name, 'website_type']}
                          label={index === 0 ? 'Website type' : ''}
                          rules={[
                            {
                              required: true,
                              message: 'Select a website type.',
                            },
                          ]}
                          className="flex-1"
                        >
                          <Select
                            placeholder="Choose"
                            options={WEBSITE_TYPE_OPTIONS}
                            optionRender={(option) => option.label}
                          />
                        </Form.Item>

                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                            className="!text-red-500"
                          />
                        )}
                      </div>
                    ))}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          d_source_name: '',
                          url: '',
                          website_type: undefined,
                        })
                      }
                      className="self-start border-dashed"
                      disabled={fields.length >= 5}
                    >
                      Add URL
                    </Button>
                  </div>
                )}
              </Form.List>
            </div>
          </div>
          <div className="mt-6 text-xs text-gray-500">
            <div className="font-medium">Notes</div>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {NOTES.link.map((note) => (
                <li key={note}>{note}</li>
              ))}
              {(sources?.length ?? 0) >= 5 && (
                <li className="text-red-500">
                  Maximum of 5 URLs allowed per submission.
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="primary"
            htmlType="submit"
            className="rounded-xl bg-[#6C63FF] px-6"
            loading={loading}
          >
            Insert
          </Button>
        </div>
      </Form>
    </>
  );
};

const TextPasteView = ({ onSubmit, loading, brandId, setFileUploading }) => {
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [textName, setTextName] = useState('');

  // Create a .txt file and upload using the same API used by useFileUploader
  const handleInsert = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      message.warning('Please paste some text first.');
      return;
    }

    try {
      setUploading(true);
      setFileUploading(true);
      // Create a Blob/File from text
      const fileName = cleanFileName(textName, 'pasted');
      const extension = 'txt';

      const blob = new Blob([trimmed], { type: 'text/plain' });
      const file = new File([blob], `${fileName}.${extension}`, {
        type: 'text/plain',
      });

      const presign = await preSendUrl(extension, {
        brand_id: brandId,
        path: 'data-sources',
        file_name: fileName,
      });
      if (!(presign?.status === 200 && presign?.data?.response)) {
        throw new Error('Failed to prepare upload.');
      }

      const putUrl = presign.data.response;
      const putResp = await fetch(putUrl, {
        method: 'PUT',
        headers: { contentType: 'text/plain' },
        body: file,
      });
      if (!putResp.ok) throw new Error('Upload failed.');
      const uploadedUrl = putResp.url?.split('?').shift();
      if (!uploadedUrl) throw new Error('Could not resolve uploaded URL.');

      onSubmit?.({
        d_source_type: 'LOCAL_FILE_UPLOAD',
        sources: [
          {
            url: uploadedUrl,
            d_source_name: textName,
          },
        ],
      });
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Unable to upload pasted text.');
    } finally {
      setUploading(false);
      setFileUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 text-gray-900">
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <label
            htmlFor="text-name"
            className="mb-1 block text-sm text-gray-700"
          >
            Name this text <span className="text-red-500">*</span>
          </label>
          <Input
            id="text-name"
            rowKey="key"
            placeholder="Enter a name for this text"
            value={textName}
            onChange={(e) => setTextName(e.target.value)}
            required
            className="w-full rounded-xl border-gray-200 focus:border-indigo-200 focus:ring-1 focus:ring-indigo-200"
          />
        </div>
        <TextArea
          autoSize={{ minRows: 6, maxRows: 10 }}
          placeholder={PLACEHOLDER_COPY['paste-text']}
          className="rounded-2xl border border-gray-200 bg-transparent text-gray-900 placeholder:text-gray-400 focus:!border-indigo-200"
          value={text}
          disabled={uploading}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="mt-6 text-xs text-gray-500">
          <div className="font-medium">Notes</div>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            {NOTES['paste-text'].map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="primary"
          className="rounded-xl bg-[#6C63FF] px-6"
          loading={loading || uploading}
          onClick={handleInsert}
          disabled={!text.trim()}
        >
          Insert
        </Button>
      </div>
    </div>
  );
};

const GoogleWorkspaceView = ({ googleDriveMessage }) => (
  <div className="flex flex-col gap-6 text-gray-900">
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
        <Space direction="vertical" size={16} className="w-full text-left">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-2xl text-indigo-600">
              <GoogleOutlined />
            </div>
            <div>
              <Title level={4} className="!m-0">
                Connect Google Drive
              </Title>
              <Text type="secondary">
                Authorise access to browse and import documents directly from
                your Google Drive.
              </Text>
            </div>
          </div>
          <Button
            type="primary"
            className="rounded-xl bg-[#6C63FF] px-6"
            onClick={googleDriveMessage}
          >
            Sign in with Google
          </Button>
        </Space>
      </div>
      <div className="mt-6 text-xs text-gray-500">
        <div className="font-medium">Notes</div>
        <ul className="mt-2 list-disc space-y-1 pl-4">
          {NOTES['google-workspace'].map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const DataSourceModal = ({
  open,
  view = 'upload',
  onClose,
  fileUploading,
  setFileUploading,
  onChangeView,
  googleDriveMessage,
}) => {
  const dispatch = useDispatch();
  const creating = useSelector((state) => state.corpus?.creating);
  const { currentBrand } = useSelector((state) => state.app);

  const { title, description } = TITLE_COPY[view] || TITLE_COPY.upload;
  const contentWrapperRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(null);

  const handleLinkSubmit = async (payload) => {
    if (!currentBrand?.brand_id) {
      message.error('Unable to determine brand. Please select a workspace.');
      return;
    }
    try {
      const resultAction = await dispatch(
        createDataSource({
          brandId: currentBrand?.brand_id,
          data: payload,
        }),
      );

      if (createDataSource.fulfilled.match(resultAction)) {
        message.success('Datasource created successfully.');
        onClose?.();
      } else {
        const errorMessage =
          resultAction.payload ||
          resultAction.error?.message ||
          'Unable to create datasource.';
        message.error(errorMessage);
        console.error(errorMessage);
      }
      return resultAction;
    } catch (error) {
      message.error('An error occurred while creating the datasource');
      console.error('Error creating datasource:', error);
      throw error;
    }
  };

  const content = useMemo(() => {
    if (view === 'google-workspace')
      return <GoogleWorkspaceView googleDriveMessage={googleDriveMessage} />;
    if (view === 'link')
      return <LinkView onSubmit={handleLinkSubmit} loading={creating} />;
    if (view === 'paste-text')
      return (
        <TextPasteView
          onSubmit={handleLinkSubmit}
          loading={creating || fileUploading}
          setFileUploading={setFileUploading}
          brandId={currentBrand?.brand_id}
        />
      );
    return (
      <UploadView
        onChangeView={onChangeView}
        onSubmit={handleLinkSubmit}
        brandId={currentBrand?.brand_id}
        fileUploading={fileUploading}
        setFileUploading={setFileUploading}
      />
    );
  }, [view, onChangeView, creating, currentBrand?.brand_id]);
  // [
  //   view,
  //   onChangeView,
  //   creating,
  //   currentBrand?.brand_id,
  //   handleLinkSubmit,
  //   googleDriveMessage,
  // ];

  useEffect(() => {
    if (!open) {
      setContentHeight(null);
      return;
    }

    const node = contentWrapperRef.current;
    if (!node) return;

    const updateHeight = () => {
      const next = node.getBoundingClientRect().height;
      setContentHeight((prev) => {
        if (prev === null) return next;
        if (Math.abs(prev - next) < 1) return prev;
        return next;
      });
    };

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateHeight();
      });
      observer.observe(node);
    } else {
      const interval = window.setInterval(updateHeight, 120);
      observer = {
        disconnect: () => window.clearInterval(interval),
      };
    }

    updateHeight();

    return () => {
      observer?.disconnect?.();
    };
  }, [open, view]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      footer={null}
      width={920}
      className="corpus-modal"
      styles={{ padding: 0, background: 'transparent' }}
      destroyOnHidden
    >
      {(creating || fileUploading) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(54, 54, 54, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            borderRadius: '8px',
            pointerEvents: 'auto',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 48, color: '#fff' }} spin />
            }
          />
        </div>
      )}
      <div className="relative overflow-hidden rounded-3xl bg-white p-10 text-gray-900 shadow-xl-">
        <style>
          {`
            .modal-body-height-wrapper {
              transition: height 0.32s ease;
            }
          `}
        </style>
        <div className="relative flex flex-col gap-6">
          <div className="flex items-start justify-between gap-4">
            <Space size={16} align="start">
              <div>
                <div className="flex flex-row items-center gap-3 mb-2">
                  {(view === 'link' ||
                    view === 'paste-text' ||
                    view === 'google-workspace') && (
                    <Button
                      type="text"
                      icon={
                        <ArrowLeftOutlined
                          style={{
                            fontSize: '24px',
                          }}
                        />
                      }
                      onClick={() => onChangeView?.('upload')}
                      className="!text-gray-500 hover:!text-gray-700"
                    />
                  )}
                  <Title level={2} style={{ margin: 0 }}>
                    {title}
                  </Title>
                </div>
                <Text type="secondary">{description}</Text>
              </div>
            </Space>
          </div>
          <div
            className="modal-body-height-wrapper"
            style={{
              height: contentHeight === null ? 'auto' : `${contentHeight}px`,
            }}
          >
            <div ref={contentWrapperRef}>{content}</div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const FileTypesPopover = () => {
  const fileTypes = [
    {
      key: 'pdf',
      name: 'Portable Document Format',
      extension: '.pdf',
      size: '50MB',
    },
    {
      key: 'docx',
      name: 'Microsoft Word Document',
      extension: '.docx',
      size: '50MB',
    },
    {
      key: 'pptx',
      name: 'Microsoft PowerPoint',
      extension: '.pptx',
      size: '10MB',
    },
    { key: 'txt', name: 'Plain Text File', extension: '.txt', size: '10MB' },
    { key: 'md', name: 'Markdown File', extension: '.md', size: '10MB' },
    { key: 'html', name: 'HyperText Markup', extension: '.html', size: '10MB' },
    {
      key: 'json',
      name: 'JavaScript Object Notation',
      extension: '.json, .jsonl, .ndjson',
      size: '10MB',
    },
  ];

  const columns = [
    {
      title: 'File Type',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text) => <div className="whitespace-normal">{text}</div>,
    },
    {
      title: 'Extension',
      dataIndex: 'extension',
      key: 'extension',
      render: (text) => <code>{text}</code>,
    },
    {
      title: 'Max Size',
      dataIndex: 'size',
      className: 'max-w-[100px] w-[100px]',
      key: 'size',
    },
    {
      title: 'Limitations',
      key: 'limitations',
      render: (_, record) => {
        return (
          <div>
            {['.pdf', '.docx'].includes(record.extension.toLowerCase()) ? (
              <Text>Max 500 pages</Text>
            ) : (
              <div className="flex items-center justify-center">
                <Text type="secondary">-</Text>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Popover
      placement="rightTop"
      arrow={false}
      content={
        <div className="p-0">
          <Table
            size="small"
            pagination={false}
            dataSource={fileTypes}
            columns={columns}
            className="[&_.ant-table]:rounded-lg [&_.ant-table]:border [&_.ant-table]:border-gray-200 [&_.ant-table]:overflow-hidden [&_.ant-table-tbody>tr>td]:border-gray-100 [&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-500 [&_.ant-table-thead>tr>th]:font-medium"
          />
        </div>
      }
      trigger="hover"
    >
      <InfoCircleOutlined className="text-gray-400 cursor-help" />
    </Popover>
  );
};

export default DataSourceModal;
